import { Checkbox } from '@progress/kendo-react-inputs'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledButton } from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { BrandTreeViewBoxInner } from '~/components/features/BrandTreeViewBox/BrandTreeViewBoxInner'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryAvailableBrandGroups } from '~/hooks/useSurveyQuery/useSurveyQueryAvailableBrandGroups'

import { useBasicOutputWizard } from '../hooks'

type BrandSelectionStepProps = {
  onBack: () => void
  onNext: () => void
}

// Fixed so the ブランドグループ全体 table and the individual-brand table (whose
// first columns differ in content and thus width) line up their checkbox
// columns instead of drifting per table-layout: auto's independent sizing.
const LABEL_COLUMN_WIDTH = '280px'
const CHECKBOX_COLUMN_WIDTH = '140px'

export const BrandSelectionStep = ({
  onBack,
  onNext,
}: BrandSelectionStepProps) => {
  const brandGroups = useSurveyQueryAvailableBrandGroups()
  const { t } = useTranslation()
  const logEvent = useLogEvent()
  const {
    brandGroupId,
    brandRoleContextActive,
    hasAnyQuestionSelected,
    isBrandSelectionValid,
    isEntireBrandGroupSelected,
    keyCompetitorBrandIds,
    ownBrandIds,
    selectedBrandIds,
    setIsEntireBrandGroupSelected,
    setKeyCompetitorBrandIds,
    setOwnBrandIds,
    setSelectedBrandIds,
  } = useBasicOutputWizard()

  const brands = React.useMemo(() => {
    const brandGroup = brandGroups.find(({ id }) => id === brandGroupId)

    return brandGroup?.brands ?? []
  }, [brandGroupId, brandGroups])

  // 自社 and 注目競合 are mutually exclusive — a brand already on one side is
  // dropped from the other's candidate pool so it can't be picked on both.
  const ownSelectableBrands = React.useMemo(
    () => brands.filter((brand) => !keyCompetitorBrandIds.includes(brand.id)),
    [brands, keyCompetitorBrandIds],
  )
  const keyCompetitorSelectableBrands = React.useMemo(
    () => brands.filter((brand) => !ownBrandIds.includes(brand.id)),
    [brands, ownBrandIds],
  )

  const handleToggleSelection = (brandId: number) => {
    const willSelect = !selectedBrandIds.includes(brandId)
    setSelectedBrandIds((prev: number[]) =>
      prev.includes(brandId)
        ? prev.filter((id) => id !== brandId)
        : [...prev, brandId],
    )
    logEvent('Select individual brand', {
      eventProperties: [{ key: 'selected', value: String(willSelect) }],
    })
  }

  // BrandTreeViewBoxInner は変更後の配列しか渡さないので、選択したブランド単体は
  // 特定できない。TreeView は1クリックで1件しか動かないため件数の増減で選択/解除を判定する。
  const handleOwnBrandIdsChange = (brandIds: number[]) => {
    setOwnBrandIds(brandIds)
    logEvent('Select own brand', {
      eventProperties: [
        {
          key: 'selected',
          value: String(brandIds.length > ownBrandIds.length),
        },
      ],
    })
  }

  const handleKeyCompetitorBrandIdsChange = (brandIds: number[]) => {
    setKeyCompetitorBrandIds(brandIds)
    logEvent('Select key competitor brand', {
      eventProperties: [
        {
          key: 'selected',
          value: String(brandIds.length > keyCompetitorBrandIds.length),
        },
      ],
    })
  }

  return (
    <VStack spacing={4}>
      <LabelingContainer label={t('basic_output_brand_group_label')}>
        <VStack spacing={4}>
          <table
            style={{
              alignSelf: 'flex-start',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
              width: 'auto',
            }}
          >
            <colgroup>
              <col style={{ width: LABEL_COLUMN_WIDTH }} />
              <col style={{ width: CHECKBOX_COLUMN_WIDTH }} />
            </colgroup>
            <thead>
              <tr
                style={{
                  borderBottom: '2px solid var(--colorBorderLight)',
                }}
              >
                <th style={{ padding: '8px 16px 8px 0' }} />
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <Typography variant="small">
                    {t('basic_output_brand_target_label')}
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '8px 16px 8px 0' }}>
                  <label
                    htmlFor="whole-brand-group-select"
                    style={{ cursor: 'pointer' }}
                  >
                    <Typography variant="normal">
                      {t('basic_output_whole_brand_group_label')}
                    </Typography>
                  </label>
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <Checkbox
                    checked={isEntireBrandGroupSelected}
                    id="whole-brand-group-select"
                    onChange={(e) => {
                      setIsEntireBrandGroupSelected(e.value)
                      logEvent('Select whole brand group', {
                        eventProperties: [
                          { key: 'selected', value: String(e.value) },
                        ],
                      })
                    }}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          {/* Activation condition documented on brandRoleContextActive in useBasicOutputWizard. */}
          {brandRoleContextActive && (
            <VStack spacing={2}>
              <LabelingContainer label={t('basic_output_brand_own_label')}>
                <BrandTreeViewBoxInner
                  brands={ownSelectableBrands}
                  selectedBrandIds={ownBrandIds}
                  onSelectedBrandIdChange={handleOwnBrandIdsChange}
                />
              </LabelingContainer>
              <LabelingContainer
                label={t('basic_output_brand_key_competitor_label')}
              >
                <BrandTreeViewBoxInner
                  brands={keyCompetitorSelectableBrands}
                  selectedBrandIds={keyCompetitorBrandIds}
                  onSelectedBrandIdChange={handleKeyCompetitorBrandIdsChange}
                />
              </LabelingContainer>
            </VStack>
          )}

          <LabelingContainer
            label={t('basic_output_individual_brand_selection_label')}
          >
            <table
              style={{
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
                width: 'auto',
              }}
            >
              <colgroup>
                <col style={{ width: LABEL_COLUMN_WIDTH }} />
                <col style={{ width: CHECKBOX_COLUMN_WIDTH }} />
              </colgroup>
              <thead>
                <tr
                  style={{
                    borderBottom: '2px solid var(--colorBorderLight)',
                  }}
                >
                  <th style={{ padding: '8px 16px 8px 0' }} />
                  <th style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <Typography variant="small">
                      {t('basic_output_brand_target_label')}
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody>
                {brands.map((brand) => {
                  const isSelected = selectedBrandIds.includes(brand.id)
                  const selectionCheckboxId = `brand-select-${brand.id}`

                  return (
                    <tr
                      key={brand.id}
                      style={{
                        borderBottom: '1px solid var(--colorBorderLight)',
                      }}
                    >
                      <td style={{ padding: '8px 16px 8px 0' }}>
                        {/* Native <label htmlFor> makes the full brand name a
                          click target for the 生成対象 checkbox and provides
                          its accessible name. */}
                        <label
                          htmlFor={selectionCheckboxId}
                          style={{ cursor: 'pointer' }}
                        >
                          <Typography variant="normal">{brand.name}</Typography>
                        </label>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <Checkbox
                          checked={isSelected}
                          disabled={!hasAnyQuestionSelected}
                          id={selectionCheckboxId}
                          onChange={() => handleToggleSelection(brand.id)}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {!hasAnyQuestionSelected && (
              <Typography color="error" variant="small">
                {t('basic_output_no_question_selected_warning_step2')}
              </Typography>
            )}
          </LabelingContainer>
        </VStack>
      </LabelingContainer>

      <div style={{ display: 'flex', gap: '8px' }}>
        <StyledButton buttonStyle="normal" onClick={onBack}>
          {t('basic_output_back')}
        </StyledButton>
        <StyledButton
          buttonStyle="success"
          disabled={!isBrandSelectionValid}
          onClick={onNext}
        >
          {t('basic_output_next')}
        </StyledButton>
      </div>
    </VStack>
  )
}
